import React, { useState, useEffect } from 'react';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import ContractContextNavigation from '../../components/ui/ContractContextNavigation';
import SidebarDocumentNavigator from '../../components/ui/SidebarDocumentNavigator';

// Import all components
import DocumentUploadZone from './components/DocumentUploadZone';
import DocumentList from './components/DocumentList';
import FolderOrganizer from './components/FolderOrganizer';
import VersionComparison from './components/VersionComparison';
import DocumentPreview from './components/DocumentPreview';
import AuditTrail from './components/AuditTrail';

const DocumentManagement = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('root');
  const [showVersionComparison, setShowVersionComparison] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [comparisonDocument, setComparisonDocument] = useState(null);
  const [activeTab, setActiveTab] = useState('documents');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Mock data for documents
  const mockDocuments = [
    {
      id: 'doc-1',
      name: 'Master Service Agreement.pdf',
      category: 'Original Contract',
      version: '2.1',
      status: 'current',
      size: 2457600, // 2.4 MB in bytes
      modified: '01/15/2024',
      uploadedBy: 'John Doe',
      author: 'Legal Team'
    },
    {
      id: 'doc-2',
      name: 'Amendment 01 - Scope Change.pdf',
      category: 'Amendments',
      version: '1.0',
      status: 'current',
      size: 876544, // 856 KB in bytes
      modified: '02/20/2024',
      uploadedBy: 'Sarah Wilson',
      author: 'Contract Manager'
    },
    {
      id: 'doc-3',
      name: 'Statement of Work.docx',
      category: 'Supporting Documents',
      version: '1.3',
      status: 'draft',
      size: 1258291, // 1.2 MB in bytes
      modified: '03/10/2024',
      uploadedBy: 'Michael Chen',
      author: 'Project Manager'
    },
    {
      id: 'doc-4',
      name: 'Pricing Schedule.xlsx',
      category: 'Supporting Documents',
      version: '2.0',
      status: 'current',
      size: 455680, // 445 KB in bytes
      modified: '01/18/2024',
      uploadedBy: 'John Doe',
      author: 'Finance Team'
    },
    {
      id: 'doc-5',
      name: 'Compliance Checklist.pdf',
      category: 'Supporting Documents',
      version: '1.1',
      status: 'archived',
      size: 694272, // 678 KB in bytes
      modified: '01/20/2024',
      uploadedBy: 'Sarah Wilson',
      author: 'Compliance Officer'
    }
  ];

  // Mock data for folders
  const mockFolders = [
    {
      id: 'root',
      name: 'All Documents',
      type: 'root',
      count: 24,
      children: []
    },
    {
      id: 'contracts',
      name: 'Original Contracts',
      type: 'contracts',
      count: 8,
      children: []
    },
    {
      id: 'amendments',
      name: 'Amendments',
      type: 'amendments',
      count: 6,
      children: []
    },
    {
      id: 'exhibits',
      name: 'Exhibits & Attachments',
      type: 'exhibits',
      count: 4,
      children: []
    },
    {
      id: 'correspondence',
      name: 'Correspondence',
      type: 'correspondence',
      count: 6,
      children: []
    }
  ];

  // Mock data for document versions
  const mockVersions = [
    {
      id: 'v2.1',
      number: '2.1',
      date: '01/15/2024',
      author: 'John Doe',
      isCurrent: true,
      isDraft: false
    },
    {
      id: 'v2.0',
      number: '2.0',
      date: '12/20/2023',
      author: 'Sarah Wilson',
      isCurrent: false,
      isDraft: false
    },
    {
      id: 'v1.5',
      number: '1.5',
      date: '11/15/2023',
      author: 'Michael Chen',
      isCurrent: false,
      isDraft: false
    },
    {
      id: 'v1.0',
      number: '1.0',
      date: '10/01/2023',
      author: 'Legal Team',
      isCurrent: false,
      isDraft: false
    }
  ];

  const handleFileUpload = async (files) => {
    setIsUploading(true);
    
    // Simulate upload process
    setTimeout(() => {
      console.log('Files uploaded:', files);
      setIsUploading(false);
      // In a real app, you would update the documents list here
    }, 2000);
  };

  const handleDocumentAction = (action, document) => {
    switch (action) {
      case 'preview':
        setPreviewDocument(document);
        setShowDocumentPreview(true);
        break;
      case 'download':
        console.log('Downloading document:', document.name);
        break;
      case 'share': console.log('Sharing document:', document.name);
        break;
      case 'compare':
        setComparisonDocument(document);
        setShowVersionComparison(true);
        break;
      case 'more': console.log('More options for document:', document.name);
        break;
      default:
        console.log(`${action} action for document:`, document.name);
    }
  };

  const handleFolderAction = (action, folder) => {
    switch (action) {
      case 'create': console.log('Creating folder:', folder?.name);
        break;
      case 'rename': console.log('Renaming folder:', folder?.name);
        break;
      case 'delete':
        console.log('Deleting folder:', folder?.name);
        break;
      default:
        console.log(`${action} action for folder:`, folder?.name);
    }
  };

  const handleVersionSelect = (versionId) => {
    console.log('Selected version:', versionId);
  };

  const handlePreviewAction = (action, document) => {
    switch (action) {
      case 'download':
        console.log('Downloading from preview:', document.name);
        break;
      case 'share': console.log('Sharing from preview:', document.name);
        break;
      case 'print': console.log('Printing document:', document.name);
        break;
      case 'rotate': console.log('Rotating document view');
        break;
      default:
        console.log(`${action} action from preview:`, document.name);
    }
  };

  // Set page title
  useEffect(() => {
    document.title = 'Document Management - MADi Contract Manager';
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ContractContextNavigation />
      <div className="pt-32 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Document Management</h1>
              <p className="text-muted-foreground mt-2">
                Organize, version, and collaborate on contract documents
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsSidebarOpen(true)}
                iconName="FolderOpen"
                iconPosition="left"
              >
                Browse Files
              </Button>
              <Button
                variant="outline"
                iconName="History"
                iconPosition="left"
                onClick={() => setActiveTab('audit')}
              >
                Audit Trail
              </Button>
              <Button
                variant="default"
                iconName="Upload"
                iconPosition="left"
                onClick={() => document.querySelector('input[type="file"]')?.click()}
              >
                Upload Documents
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-foreground">24</p>
                  <p className="text-sm text-muted-foreground">Total Documents</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-foreground">12.4 MB</p>
                  <p className="text-sm text-muted-foreground">Total Size</p>
                </div>
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="HardDrive" size={24} className="text-success" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-foreground">8</p>
                  <p className="text-sm text-muted-foreground">Active Versions</p>
                </div>
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="GitBranch" size={24} className="text-warning" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-foreground">156</p>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                </div>
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Eye" size={24} className="text-accent" />
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border mb-8">
            <nav className="flex space-x-8">
              {[
                { id: 'documents', label: 'Documents', icon: 'FileText' },
                { id: 'folders', label: 'Folders', icon: 'FolderOpen' },
                { id: 'audit', label: 'Audit Trail', icon: 'Activity' }
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 transition-legal-fast ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="space-y-8">
            {activeTab === 'documents' && (
              <>
                <DocumentUploadZone 
                  onFileUpload={handleFileUpload}
                  isUploading={isUploading}
                />
                <DocumentList
                  documents={mockDocuments}
                  onDocumentAction={handleDocumentAction}
                  selectedDocuments={selectedDocuments}
                  onDocumentSelect={setSelectedDocuments}
                />
              </>
            )}

            {activeTab === 'folders' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  <FolderOrganizer
                    folders={mockFolders}
                    onFolderAction={handleFolderAction}
                    selectedFolder={selectedFolder}
                    onFolderSelect={setSelectedFolder}
                  />
                </div>
                <div className="lg:col-span-2">
                  <DocumentList
                    documents={mockDocuments}
                    onDocumentAction={handleDocumentAction}
                    selectedDocuments={selectedDocuments}
                    onDocumentSelect={setSelectedDocuments}
                  />
                </div>
              </div>
            )}

            {activeTab === 'audit' && (
              <AuditTrail documentId="CT-2024-001" />
            )}
          </div>

          {/* Quick Actions */}
          <div className="fixed bottom-6 right-6 flex flex-col space-y-3">
            <Button
              variant="default"
              size="icon"
              onClick={() => handleDocumentAction('compare', mockDocuments?.[0])}
              className="w-12 h-12 rounded-full shadow-legal-lg"
              title="Compare Versions"
            >
              <Icon name="GitCompare" size={20} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setActiveTab('audit')}
              className="w-12 h-12 rounded-full shadow-legal-lg bg-card"
              title="View Audit Trail"
            >
              <Icon name="History" size={20} />
            </Button>
          </div>
        </div>
      </div>
      {/* Modals and Overlays */}
      {showVersionComparison && comparisonDocument && (
        <VersionComparison
          document={comparisonDocument}
          versions={mockVersions}
          onVersionSelect={handleVersionSelect}
          onClose={() => {
            setShowVersionComparison(false);
            setComparisonDocument(null);
          }}
        />
      )}
      {showDocumentPreview && previewDocument && (
        <DocumentPreview
          document={previewDocument}
          onClose={() => {
            setShowDocumentPreview(false);
            setPreviewDocument(null);
          }}
          onAction={handlePreviewAction}
        />
      )}
      <SidebarDocumentNavigator
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        contractId="CT-2024-001"
      />
    </div>
  );
};

export default DocumentManagement;